import React, { useEffect, useState } from 'react';
import {
  ContratoEspecialTableData,
  updateStatusContratoEspecial,
  getDataByContratoId,
} from '@app/api/contratoEspecial';
import { hasPermission } from '@app/helpers/permissions';
import { Icon } from 'antd';
import { Table, ColumnProps, FormSwitch, Form, Row, FormInput, Col, Button, FormResetButton } from '@digital/ui';
import { compareBool, compareText } from '@app/helpers/comparators';

interface TabelaProps {
  selectedContratoId: string;
}

const TabelaContratoEspecial = (props: TabelaProps) => {
  const { selectedContratoId } = props;
  const [tableData, setTableData] = useState<ContratoEspecialTableData[]>();
  const [updated, setUpdated] = useState<boolean>(false);

  useEffect(() => {
    const getTableData = async (id: string) => {
      id = id.replace('/', '-');
      const result = await getDataByContratoId(id);
      if (result) {
        setTableData(result);
      }
    };
    if (selectedContratoId) getTableData(selectedContratoId);
  }, [selectedContratoId, updated]);

  const handleChange = async (id: string, especial: boolean) => {
    const result = await updateStatusContratoEspecial(id, especial);
    setUpdated(!updated);
  };

  const columns: ColumnProps<ContratoEspecialTableData>[] = [
    {
      title: 'N',
      dataIndex: 'indice',
      width: '1%',
    },
    {
      title: 'Contrato/Pcs',
      dataIndex: 'numeroContrato',
      width: '10%',
      sorter: (a, b) => compareText(a.numeroContrato, b.numeroContrato),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar Número Contrato"
              name="numeroContrato"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => { })}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';

        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) =>
        record.numeroContrato &&
        record.numeroContrato
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase()),
    },
    {
      title: 'Nome Fornecedor',
      dataIndex: 'nomeFornecedor',
      width: '45%',
      sorter: (a, b) => compareText(a.nomeFornecedor, b.nomeFornecedor),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar Nome do Fornecedor"
              name="nomeFornecedor"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => { })}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';

        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) =>
        record.nomeFornecedor &&
        record.nomeFornecedor
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase()),
    },
    {
      title: 'Nome Matriz',
      dataIndex: 'nomeMatriz',
      width: '20%',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar Nome Matriz"
              name="nomeMatriz"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => { })}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';

        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) =>
        record.nomeMatriz &&
        record.nomeMatriz
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase()),
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      width: '20%',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar CNPJ"
              name="cnpj"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => { })}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';

        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) =>
        record.cnpj &&
        record.cnpj
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase()),
    },
    {
      title: 'Código',
      dataIndex: 'codigoFornecedor',
      width: '10%',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar Código do Fornecedor"
              name="codigoFornecedor"
              style={{ marginTop: '10px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => { })}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';

        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) =>
        record.codigoFornecedor &&
        record.codigoFornecedor
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase()),
    },
    {
      title: 'Especial',
      dataIndex: 'especial',
      key: 'especial',
      width: '5%',
      sorter: (a, b) => compareBool(a.especial, b.especial),
      render: (especial: boolean, item: ContratoEspecialTableData) => (
        <FormSwitch
          name="especial"
          disabled={!hasPermission(['Dono', 'CONTRATOS_ESPECIAIS_EDITAR'])}
          checked={especial}
          onChange={e => handleChange(item.id, e.valueOf())}
          span={8}
        />
      ),
    },
  ];

  return (
    <>
      <Table dataSource={tableData} columns={columns} rowKey="id" pagination={true} />
    </>
  );
};

export default TabelaContratoEspecial;
